import { ReactNode } from 'react'

/**
 * Shared presentational blocks for the Q12 results dashboards. Used by both
 * the portal report (Raportid → Q12 tulemused) and the admin results tool —
 * the two SPAs may not import from each other, so these live in common.
 * All copy arrives via props; data shapes mirror `Q12ResultsPayload`
 * (src/server/services/q12/results-payload.ts).
 *
 * Styling: q12-results.css, pulled in via each app's index.scss (the same
 * pattern as date-picker.css).
 */

const formatAverage = (value: number | null): string => (value === null ? '—' : value.toFixed(2))

const deltaClass = (change: number): string =>
    change > 0 ? 'q12r-delta--up' : change < 0 ? 'q12r-delta--down' : 'q12r-delta--flat'

const Delta = ({ change, decimals = 2 }: { change: number | null; decimals?: number }) => {
    if (change === null) return null
    const arrow = change > 0 ? '▲' : change < 0 ? '▼' : '•'
    const formatted = `${change > 0 ? '+' : ''}${change.toFixed(decimals)}`
    return (
        <span className={`q12r-delta ${deltaClass(change)}`}>
            {arrow} {formatted}
        </span>
    )
}

// ── Stat tiles ──────────────────────────────────────────────────────────────

/**
 * "answered / eligible" label. Eligible is 0 when the campaign has no groups
 * (legacy import) — that is "unknown", shown as a dash rather than 0.
 */
export const formatOutOf = (done: number, eligible: number): string =>
    `${done} / ${eligible > 0 ? eligible : '—'}`

export interface Q12StatTile {
    label: string
    value: string
    /** Numeric change vs the comparison period; renders a colored ▲/▼ chip. */
    delta?: number | null
    /** Small print under the value (e.g. "5 / 8 vastajat"). */
    sub?: string
}

export const Q12StatTiles = ({ tiles }: { tiles: Q12StatTile[] }) => (
    <div className="q12r-tiles">
        {tiles.map((tile) => (
            <div key={tile.label} className="q12r-tile">
                <span className="q12r-tile__label">{tile.label}</span>
                <span className="q12r-tile__value">
                    {tile.value}
                    {tile.delta !== undefined && <Delta change={tile.delta} />}
                </span>
                {tile.sub ? <span className="q12r-tile__sub">{tile.sub}</span> : null}
            </div>
        ))}
    </div>
)

// ── Participation bars ──────────────────────────────────────────────────────

export interface Q12ParticipationRow {
    label: string
    done: number
    total: number
}

export const Q12ParticipationBars = ({ rows }: { rows: Q12ParticipationRow[] }) => (
    <div className="q12r-participation">
        {rows.map((row) => {
            const pct = row.total > 0 ? Math.min(100, (row.done / row.total) * 100) : 0
            return (
                <div key={row.label} className="q12r-participation__row">
                    <span className="q12r-participation__label">{row.label}</span>
                    <span className="q12r-participation__track">
                        <span className="q12r-participation__fill" style={{ width: `${pct}%` }} />
                    </span>
                    <span className="q12r-participation__count">
                        {row.done}/{row.total}
                    </span>
                </div>
            )
        })}
    </div>
)

// ── Question results table ──────────────────────────────────────────────────

export interface Q12QuestionRow {
    questionId: number
    name: string
    responseCount: number
    average: number | null
    previousAverage: number | null
    change: number | null
}

export interface Q12QuestionTableLabels {
    question: string
    responses: string
    average: string
    previous: string
}

export const Q12QuestionTable = ({
    rows,
    labels,
    showPrevious,
}: {
    rows: Q12QuestionRow[]
    labels: Q12QuestionTableLabels
    showPrevious: boolean
}) => (
    <div className="q12r-table-wrap">
        <table className="q12r-table">
            <thead>
                <tr>
                    <th className="q12r-table__question">{labels.question}</th>
                    <th className="q12r-table__num">{labels.responses}</th>
                    <th className="q12r-table__bar-col">{labels.average}</th>
                    {showPrevious ? <th className="q12r-table__num">{labels.previous}</th> : null}
                </tr>
            </thead>
            <tbody>
                {rows.map((row) => (
                    <tr key={row.questionId}>
                        <td className="q12r-table__question">{row.name}</td>
                        <td className="q12r-table__num">{row.responseCount}</td>
                        <td className="q12r-table__bar-col">
                            <span className="q12r-score">
                                <span className="q12r-score__track">
                                    <span
                                        className="q12r-score__fill"
                                        style={{
                                            width:
                                                row.average === null
                                                    ? 0
                                                    : `${(row.average / 5) * 100}%`,
                                        }}
                                    />
                                </span>
                                <span className="q12r-score__value">
                                    {formatAverage(row.average)}
                                </span>
                                <Delta change={row.change} />
                            </span>
                        </td>
                        {showPrevious ? (
                            <td className="q12r-table__num">
                                {formatAverage(row.previousAverage)}
                            </td>
                        ) : null}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
)

// ── Free-text answers ───────────────────────────────────────────────────────

export interface Q12TextAnswerGroup {
    questionId: number
    name: string
    answers: string[]
}

/** Anonymous quotes per free-text question; renders nothing without answers. */
export const Q12TextAnswers = ({
    title,
    blocks,
    countLabel,
}: {
    title: string
    blocks: Q12TextAnswerGroup[]
    countLabel: (n: number) => string
}) =>
    blocks.length === 0 ? null : (
        <section className="q12r-section q12r-text-answers">
            <h2 className="q12r-section__title">{title}</h2>
            {blocks.map((block) => (
                <div key={block.questionId} className="q12r-text-answers__question">
                    <h3 className="q12r-text-answers__name">
                        {block.name}
                        <span className="q12r-text-answers__count">
                            {countLabel(block.answers.length)}
                        </span>
                    </h3>
                    <ul className="q12r-text-answers__list">
                        {block.answers.map((answer, i) => (
                            <li key={i} className="q12r-text-answers__item">
                                {answer}
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </section>
    )

// ── History trend ───────────────────────────────────────────────────────────

export interface Q12TrendPoint {
    campaignId: number
    label: string
    overallAverage: number | null
    isActive: boolean
}

export const Q12Trend = ({
    points,
    onSelect,
}: {
    points: Q12TrendPoint[]
    onSelect?: (campaignId: number) => void
}) => {
    // Fixed drawing box, scaled by the viewBox. Scores live on a 1–5 scale;
    // the axis floor follows the data (one step below the lowest score) so
    // period-to-period movement stays readable.
    const W = 640
    const H = 200
    const padL = 56
    const padR = 56
    const padT = 36
    const padB = 34
    const scored = points.map((p) => p.overallAverage).filter((v): v is number => v !== null)
    const min = scored.length > 0 ? Math.max(1, Math.floor(Math.min(...scored)) - 1) : 1
    const max = 5
    const n = points.length
    const x = (i: number): number =>
        n <= 1 ? padL + (W - padL - padR) / 2 : padL + (i * (W - padL - padR)) / (n - 1)
    const y = (value: number): number =>
        padT + (1 - (value - min) / (max - min)) * (H - padT - padB)

    // Contiguous scored runs become line segments; a hidden/empty period breaks the line.
    const segments: string[] = []
    let run: string[] = []
    points.forEach((point, i) => {
        if (point.overallAverage === null) {
            if (run.length > 1) segments.push(run.join(' '))
            run = []
        } else {
            run.push(`${x(i)},${y(point.overallAverage)}`)
        }
    })
    if (run.length > 1) segments.push(run.join(' '))

    const ticks = Array.from({ length: max - min + 1 }, (_, i) => min + i)
    const select = (campaignId: number) => (onSelect ? () => onSelect(campaignId) : undefined)

    return (
        <div className="q12r-trendline">
            <svg className="q12r-trendline__svg" viewBox={`0 0 ${W} ${H}`} role="img">
                {ticks.map((tick) => (
                    <g key={tick}>
                        <line
                            className="q12r-trendline__grid"
                            x1={padL}
                            x2={W - padR}
                            y1={y(tick)}
                            y2={y(tick)}
                        />
                        <text
                            className="q12r-trendline__tick"
                            x={padL - 10}
                            y={y(tick) + 4}
                            textAnchor="end"
                        >
                            {tick}
                        </text>
                    </g>
                ))}
                {segments.map((segment, i) => (
                    <polyline key={i} className="q12r-trendline__line" points={segment} />
                ))}
                {points.map((point, i) =>
                    point.overallAverage === null ? null : (
                        <g
                            key={point.campaignId}
                            className={`q12r-trendline__point${
                                point.isActive ? ' q12r-trendline__point--active' : ''
                            }`}
                            onClick={select(point.campaignId)}
                            role={onSelect ? 'button' : undefined}
                            tabIndex={onSelect ? 0 : undefined}
                        >
                            <circle
                                cx={x(i)}
                                cy={y(point.overallAverage)}
                                r={point.isActive ? 7 : 5}
                            />
                            <text
                                className="q12r-trendline__value"
                                x={x(i)}
                                y={y(point.overallAverage) - 12}
                                textAnchor="middle"
                            >
                                {formatAverage(point.overallAverage)}
                            </text>
                        </g>
                    ),
                )}
                {points.map((point, i) => (
                    <text
                        key={point.campaignId}
                        className={`q12r-trendline__label${
                            point.isActive ? ' q12r-trendline__label--active' : ''
                        }`}
                        x={x(i)}
                        y={H - padB + 22}
                        textAnchor="middle"
                        onClick={select(point.campaignId)}
                    >
                        {point.label}
                    </text>
                ))}
            </svg>
        </div>
    )
}

// ── Ring (headline percentage) ──────────────────────────────────────────────

export interface Q12RingProps {
    /** 0–100, or null when nobody is eligible. */
    percent: number | null
    label: string
    /** Small print under the label (e.g. "25 / 27"). */
    sub?: string
    /** Percentage-point change vs the comparison period. */
    delta?: number | null
    /** Text after the delta chip (e.g. "vs eelmine periood"). */
    deltaSuffix?: string
}

export const Q12Ring = ({ percent, label, sub, delta, deltaSuffix }: Q12RingProps) => {
    const radius = 44
    const circumference = 2 * Math.PI * radius
    const clamped = percent === null ? 0 : Math.max(0, Math.min(100, percent))
    return (
        <div className="q12r-ring">
            <div className="q12r-ring__chart">
                <svg className="q12r-ring__svg" viewBox="0 0 100 100" aria-hidden="true">
                    <circle className="q12r-ring__track" cx="50" cy="50" r={radius} />
                    {clamped > 0 ? (
                        <circle
                            className="q12r-ring__fill"
                            cx="50"
                            cy="50"
                            r={radius}
                            strokeDasharray={`${(clamped / 100) * circumference} ${circumference}`}
                            transform="rotate(-90 50 50)"
                        />
                    ) : null}
                </svg>
                <div className="q12r-ring__body">
                    <span className="q12r-ring__value">
                        {percent === null ? '—' : `${Math.round(percent)}%`}
                    </span>
                    {delta !== undefined && delta !== null ? (
                        <span className="q12r-ring__delta">
                            <Delta change={delta} decimals={0} />
                            {deltaSuffix ? (
                                <span className="q12r-ring__delta-suffix">{deltaSuffix}</span>
                            ) : null}
                        </span>
                    ) : null}
                </div>
            </div>
            <span className="q12r-ring__label">{label}</span>
            {sub ? <span className="q12r-ring__sub">{sub}</span> : null}
        </div>
    )
}

// ── Programme overview card ─────────────────────────────────────────────────

export interface Q12ProgrammeOverviewProps {
    title: string
    status?: { label: string; tone: 'active' | 'ended' | 'upcoming' }
    /** Campaign or team name. Omitted together with `placeholder`. */
    headline?: string
    /** e.g. "Lõpeb 5 päeva pärast" / "Lõppes 15.04.2026". */
    subline?: string
    /** `total` null = eligible count unknown (campaign without groups). */
    responded?: { done: number; total: number | null; label: string }
    /** Participation ring + stat tiles (TASK-538 overview cards). */
    stats?: { ring: Q12RingProps; tiles: Q12StatTile[] }
    rows?: { label: string; value: ReactNode }[]
    link?: {
        label: string
        url: string
        copyLabel: string
        copiedLabel?: string
        copied?: boolean
        onCopy?: () => void
    }
    /** Hint shown instead of the body (e.g. "Vali tiim filtrist"). */
    placeholder?: string
}

export const Q12ProgrammeOverview = ({
    title,
    status,
    headline,
    subline,
    responded,
    stats,
    rows = [],
    link,
    placeholder,
}: Q12ProgrammeOverviewProps) => {
    const tone = status?.tone ?? 'ended'
    return (
        <section className="q12r-programme">
            <header className="q12r-programme__header">
                <h3 className="q12r-programme__title">{title}</h3>
                {status ? (
                    <span className={`q12r-programme__status q12r-programme__status--${tone}`}>
                        {status.label}
                    </span>
                ) : null}
            </header>
            {placeholder !== undefined ? (
                <p className="q12r-programme__placeholder">{placeholder}</p>
            ) : (
                <>
                    {headline !== undefined ? (
                        <div className="q12r-programme__hero">
                            {status ? (
                                <span
                                    className={`q12r-programme__icon q12r-programme__icon--${tone}`}
                                    aria-hidden="true"
                                >
                                    {tone === 'ended' ? '✓' : tone === 'upcoming' ? '…' : '▶'}
                                </span>
                            ) : null}
                            <div className="q12r-programme__hero-text">
                                <span className="q12r-programme__headline">{headline}</span>
                                {subline ? (
                                    <span className="q12r-programme__subline">{subline}</span>
                                ) : null}
                            </div>
                            {responded ? (
                                <div className="q12r-programme__responded">
                                    <span className="q12r-programme__responded-value">
                                        <strong>{responded.done}</strong>/{responded.total ?? '—'}
                                    </span>
                                    <span className="q12r-programme__responded-label">
                                        {responded.label}
                                    </span>
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                    {stats ? (
                        <div className="q12r-programme__stats">
                            <Q12Ring {...stats.ring} />
                            <Q12StatTiles tiles={stats.tiles} />
                        </div>
                    ) : null}
                    {rows.length > 0 || link ? (
                        <dl className="q12r-programme__rows">
                            {rows.map((row) => (
                                <div key={row.label} className="q12r-programme__row">
                                    <dt className="q12r-programme__row-label">{row.label}</dt>
                                    <dd className="q12r-programme__row-value">{row.value}</dd>
                                </div>
                            ))}
                            {link ? (
                                <div className="q12r-programme__row">
                                    <dt className="q12r-programme__row-label">{link.label}</dt>
                                    <dd className="q12r-programme__row-value q12r-programme__link">
                                        <code className="q12r-programme__url">{link.url}</code>
                                        <button
                                            type="button"
                                            className="q12r-programme__copy"
                                            onClick={link.onCopy}
                                            disabled={!link.onCopy}
                                        >
                                            {link.copied
                                                ? (link.copiedLabel ?? link.copyLabel)
                                                : link.copyLabel}
                                        </button>
                                    </dd>
                                </div>
                            ) : null}
                        </dl>
                    ) : null}
                </>
            )}
        </section>
    )
}
